import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, DollarSign, TrendingUp, Pencil } from "lucide-react";

const INCOME_CATEGORIES = [
  { value: "salary", label: "Salary" },
  { value: "rental_income", label: "Rental Income" },
  { value: "investments", label: "Investment Returns" },
  { value: "business", label: "Business Income" },
  { value: "freelance", label: "Freelance" },
  { value: "other", label: "Other" },
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
};

export default function Income() {
  const { api } = useAuth();
  const [incomes, setIncomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [formData, setFormData] = useState({
    source: "",
    amount: "",
    category: "",
    description: "",
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchIncomes();
  }, []);

  const fetchIncomes = async () => {
    try {
      const res = await api.get("/income");
      setIncomes(res.data);
    } catch (error) {
      toast.error("Failed to fetch income data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount)
      };

      if (editingIncome) {
        await api.put(`/income/${editingIncome.id}`, payload);
        toast.success("Income updated successfully");
      } else {
        await api.post("/income", payload);
        toast.success("Income added successfully");
      }
      
      setDialogOpen(false);
      setEditingIncome(null);
      setFormData({
        source: "",
        amount: "",
        category: "",
        description: "",
        date: new Date().toISOString().split('T')[0]
      });
      await fetchIncomes();
    } catch (error) {
      console.error("Income error:", error);
      toast.error(error.response?.data?.detail || "Failed to save income");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (income) => {
    setEditingIncome(income);
    setFormData({
      source: income.source || "",
      amount: income.amount?.toString() || "",
      category: income.category || "",
      description: income.description || "",
      date: income.date || new Date().toISOString().split('T')[0]
    });
    setDialogOpen(true);
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) {
      setEditingIncome(null);
      setFormData({
        source: "",
        amount: "",
        category: "",
        description: "",
        date: new Date().toISOString().split('T')[0]
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this income?")) return;
    try {
      await api.delete(`/income/${id}`);
      toast.success("Income deleted");
      fetchIncomes();
    } catch (error) {
      toast.error("Failed to delete income");
    }
  };

  const totalIncome = incomes.reduce((sum, inc) => sum + inc.amount, 0);
  const incomeByCategory = incomes.reduce((acc, inc) => {
    acc[inc.category] = (acc[inc.category] || 0) + inc.amount;
    return acc;
  }, {});

  return (
    <Layout title="Income">
      <div className="space-y-6">
        {/* Header with stats and add button */}
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div className="flex items-center gap-4">
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="total-income-card">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded bg-primary/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Total Income</p>
                  <p className="text-2xl font-bold text-primary font-mono">{formatCurrency(totalIncome)}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
            <DialogTrigger asChild>
              <Button 
                className="bg-primary text-black font-bold uppercase tracking-wide rounded-sm hover:bg-primary/90"
                data-testid="add-income-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Income
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none">
              <DialogHeader>
                <DialogTitle className="text-white" style={{ fontFamily: 'Manrope' }}>
                  {editingIncome ? "Edit Income" : "Add New Income"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Source</Label>
                  <Input
                    value={formData.source}
                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                    placeholder="e.g., Company XYZ, Rental Property"
                    required
                    data-testid="income-source-input"
                    className="bg-black/50 border-white/10 rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Amount</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="income-amount-input"
                    className="bg-black/50 border-white/10 rounded-sm font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                    required
                  >
                    <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="income-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      {INCOME_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Date</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    data-testid="income-date-input"
                    className="bg-black/50 border-white/10 rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Description (Optional)</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Additional notes"
                    data-testid="income-description-input"
                    className="bg-black/50 border-white/10 rounded-sm"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-primary text-black font-bold uppercase tracking-wide rounded-sm hover:bg-primary/90"
                  data-testid="submit-income-btn"
                >
                  Add Income
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Category breakdown */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {INCOME_CATEGORIES.map((cat) => (
            <Card key={cat.value} className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardContent className="p-4">
                <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">{cat.label}</p>
                <p className="text-lg font-bold text-white font-mono">
                  {formatCurrency(incomeByCategory[cat.value] || 0)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Income list */}
        <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="income-list">
          <CardHeader>
            <CardTitle className="text-lg text-white" style={{ fontFamily: 'Manrope' }}>
              Income History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-zinc-500">Loading...</div>
            ) : incomes.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                No income records yet. Add your first income!
              </div>
            ) : (
              <div className="space-y-2">
                {incomes.map((income) => (
                  <div
                    key={income.id}
                    className="flex items-center justify-between p-4 border border-white/5 hover:border-white/10 transition-colors"
                    data-testid={`income-item-${income.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded bg-primary/20 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{income.source}</p>
                        <p className="text-sm text-zinc-500">
                          {INCOME_CATEGORIES.find(c => c.value === income.category)?.label || income.category}
                          {income.description && ` • ${income.description}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-lg font-bold text-primary font-mono">
                          +{formatCurrency(income.amount)}
                        </p>
                        <p className="text-xs text-zinc-500">{income.date}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(income)}
                        className="text-zinc-500 hover:text-blue-500 hover:bg-blue-500/10"
                        data-testid={`edit-income-${income.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(income.id)}
                        className="text-zinc-500 hover:text-red-500 hover:bg-red-500/10"
                        data-testid={`delete-income-${income.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
